import React, {useMemo, useState} from "react";
import {Link, matchPath, useLocation} from "react-router-dom";
import htmlToDraft from "html-to-draftjs";
import {ContentState, EditorState} from "draft-js";
import { Collapse } from "antd";
import BackgroundImage from "../../components/BackgroundImage";
import { useAuth } from "../../providers/AuthProvider";


const OppQuickSummary = ({
                             client,
                             title,
                             description,
                             descriptionPath,
                             canViewJobPost = false,
                             jobInfo, ...props
                         }) => {

    const [viewFullDescription, setViewFullDescription] = useState(false);

    const {auth} = useAuth();
    const jobViewingType = auth?.canManageClientResourceTalent?.() ? 'supplier' : 'talent'

    const location = useLocation();
    const [activeKeys, setActiveKeys] = useState(['1']);

    const isViewingJobPosting = useMemo(() => {
        return matchPath(
            { path: `${jobViewingType}/job-board/job-details/:jobId`},
            location.pathname,
        )
    }, [location.pathname]);

    const plainDescription = useMemo(() => {
        if (description) {
            let cleanBreaks = TB.replace(description, /\n/g, "<p></p>");

            const blocksFromHTML = htmlToDraft(cleanBreaks);

            let editorState = EditorState.createWithContent(
                ContentState.createFromBlockArray(
                    blocksFromHTML.contentBlocks,
                    blocksFromHTML.entityMap,
                )
            )
            let plainText = editorState.getCurrentContent().getPlainText('');
            let regex = /(?:\r\n|\r|\n)/g; // new line, carriage return, line feed

            return plainText.replace(regex, '').trim();
        }
    }, [description]);

    return (
        <div className="job-brief proposal-tab-content job-brief_NEW mt-4 md:mt-0 md:h-full md:border-l">

            {/* mobile to tablet screen sizes */}
            <div className="md:hidden">
                <div>
                    <div className="flex items-center">
                        <BackgroundImage
                            srcList={[client.logo_path, TB.defaultClientLogoImage()]}
                            className="job-client-logo mr-4"
                        />

                        <div>
                            <h4 className="mb-0">{client.name}</h4>
                            <p className="position-title">{title}</p>
                        </div>
                    </div>
                </div>

                <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(keys)}
                    ghost
                    className="[&_.ant-collapse-header]:w-max [&_.ant-collapse-header]:inline-flex [&_.ant-collapse-header]:items-center bg-transparent [&_svg]:text-primary-lighter"
                >
                     <Collapse.Panel
                        key="1"
                        header={( 
                            <span className="btn btn-link-primary mb-0">
                                {activeKeys.includes('1') ? 'Hide details' : 'Show details'}
                            </span>
                         )}
                    >
                         <div className="flex flex-col gap-1">
                            {props.children}

                            <div className="flex flex-col items-stretch gap-9">
                                {/* <p className="position-title">{title}</p> */}

                                {description && (
                                    plainDescription.length > 120 ? (
                                        <div className="position-description">
                                            <span className="flex flex-col">
                                                <div className="rich-text-display">
                                                    {viewFullDescription ? (
                                                        <span dangerouslySetInnerHTML={{__html: description}}/>
                                                    ) : <p>{TB.truncate(plainDescription, {length: 120})}</p>}
                                                </div>
                                                <button className="btn btn-link btn-link-primary self-start"
                                                        onClick={() => setViewFullDescription(!viewFullDescription)}>
                                                    {!viewFullDescription ? 'View More' : 'View Less'}
                                                </button>
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="position-description">
                                            <div className="rich-text-display">
                                                <span dangerouslySetInnerHTML={{__html: description}}/>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>

                            {TB.isDefined(descriptionPath) && (
                                <div className="position-document-section">
                                    <p>
                                        Supporting Document
                                    </p>

                                    <p className="position-document">
                                        <i className="far fa-file-alt"/>
                                        <a target="_blank"
                                        href={descriptionPath.path}
                                        className="btn btn-link btn-link-primary">
                                            View/Download
                                        </a>
                                    </p>
                                </div>
                            )}

                            <hr/>

                            {TB.map(jobInfo, (value, label) => (
                                <div key={label} className="position-info-section">

                                    <p className="info-label">
                                        {label}
                                    </p>
                                    <p className="info-value">
                                        {value || (
                                            <span className="text-italic text-muted">N/A</span>
                                        )}
                                    </p>

                                </div>
                            ))}
                        </div>
                    </Collapse.Panel>
                </Collapse>
            </div>

            {/* screens wider than tablet */}
            <div className="hidden md:flex flex-col gap-2">
                <div className="flex flex-col items-start gap-1">
                    <BackgroundImage
                        srcList={[client.logo_path, TB.defaultClientLogoImage()]}
                        className="job-client-logo"/>

                    <h4 className="mb-0">{client.name}</h4>
                </div>

                {props.children}

                <div className="flex flex-col items-stretch gap-1">

                    {(!isViewingJobPosting && canViewJobPost) ? (
                        <Link className="position-title"
                              target="_blank"
                              to={`/${jobViewingType}/job-board/job-details/${props.jobId}`}>
                            <span>
                                {title}
                            </span> <i className="ml-1 fas fa-external-link-alt"/>
                        </Link>
                    ) : (
                        <p className="position-title">{title}</p>
                    )}

                    {description && (
                        plainDescription.length > 120 ? (
                            <div className="position-description">
                                <span className="flex flex-col">
                                    <span className="rich-text-display">
                                        {viewFullDescription ? (
                                            <span dangerouslySetInnerHTML={{__html: description}}/>
                                        ) : (
                                            <p>{TB.truncate(plainDescription, {length: 120})}</p>
                                        )}
                                    </span>
                                    <div className="flex flex-row justify-between gap-3 items-center">
                                        <button className="btn btn-link btn-link-primary"
                                                onClick={() => setViewFullDescription(!viewFullDescription)}>
                                            {!viewFullDescription ? 'View More' : 'View Less'}
                                        </button>
                                    </div>
                                </span>
                            </div>
                        ) : (
                            <div className="position-description">
                                <div className="rich-text-display">
                                    <span dangerouslySetInnerHTML={{__html: description}}/>
                                </div>
                            </div>
                        )
                    )}
                </div>
                {TB.isDefined(descriptionPath) && (
                    <div className="position-document-section">
                        <p>
                            Supporting Document
                        </p>

                        <p className="position-document">
                            <i className="far fa-file-alt"/>
                            <a target="_blank"
                               href={descriptionPath.path}
                               className="btn btn-link btn-link-primary">
                                View/Download
                            </a>
                        </p>
                    </div>
                )}

                <hr/>

                {TB.map(jobInfo, (value, label) => (
                    <div key={label} className="position-info-section">

                        <p className="info-label">
                            {label}
                        </p>
                        <p className="info-value">
                            {value || (
                                <span className="text-italic text-muted">N/A</span>
                            )}
                        </p>

                    </div>
                ))}
            </div>

        </div>
    )
}

const roundHourlyRate = (rate) => {
    return Math.round((rate + Number.EPSILON) * 100) / 100
}
const TalentOppQuickSummary = ({job, opportunity = null, isDisabled, onToggleInterest, onReferJob}) => {
    let {
        client,
        title,
        description,
        description_path,
        can_show_interest_in_job,
        has_shown_interest_in_job,
    } = job;

    const {auth} = useAuth();
    const jobInfo = useMemo(
        () => job?.jobInfo(opportunity),
        [job, opportunity]);

    if (job.isTimeMaterials() && !job.targetRate() && job.estimated_min_payout) {
        jobInfo['Estimated Payout'] = `${TB.formatCurrency(job.estimated_min_payout)} - ${TB.formatCurrency(job.estimated_max_payout)}`;
    }

    const canViewJobPost = opportunity?.isAvailable() && !opportunity?.isActive() && !auth?.isTalentSupplierResource()

    return (
        <OppQuickSummary
            jobId={job.id}
            client={client}
            title={title}
            description={description}
            descriptionPath={description_path}
            canViewJobPost={canViewJobPost}
            jobInfo={jobInfo}>

            {onToggleInterest && (can_show_interest_in_job || has_shown_interest_in_job) && (
                <button className={TB.mapClass("btn btn-block", {
                    "shownInterest btn-link": job.has_shown_interest_in_job,
                    "btn-primary": !job.has_shown_interest_in_job
                })}
                        disabled={isDisabled}
                        onClick={async () => onToggleInterest()}>
                    <span>{!job.has_shown_interest_in_job ? "I'm Interested" : "Withdraw Interest"}</span>
                </button>
            )}

            {onReferJob && !job.is_match && (
                <>
                    <p className="refer-help">Have a friend that may be interested?</p>
                    <button className="btn btn-block btn-outline-primary"
                            disabled={isDisabled} onClick={() => onReferJob()}>
                        Refer Someone
                    </button>
                </>
            )}

        </OppQuickSummary>
    )
}

const TeamOppQuickSummary = ({opportunity}) => {

    let {
        title,
        disciplines = [],
        project,
        // posted_at,
        client,
        team
    } = opportunity
    let {
        // client,
        location,
        description,
        assumptions,
        description_path,
    } = project;



    let jobInfo = {
        "Team": team.name,
        "Location": location?.full,
        [disciplines?.length > 1 ? "Roles" : "Role"]: TB.map(disciplines, 'name').join(', '),
        "Project Duration": project.panelTimeDisplay(),
        "Estimated Payout": TB.formatCurrency(TB.sumBy(disciplines, 'payout_amount')),
        // "Posted": posted_at && TB.moment(posted_at).fromNow()
    }

    return (
        <OppQuickSummary
            client={client}
            title={title}
            description={description || assumptions}
            descriptionPath={description_path}
            jobInfo={jobInfo}/>
    )
}

export {
    TalentOppQuickSummary,
    TeamOppQuickSummary
}